import React, { useState, useEffect, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/sonner";
import {
  DollarSign,
  Save,
  Settings2,
  Star,
  Loader2,
  RefreshCw,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const BILLING_LABELS = {
  one_time: "One-time",
  per_unit: "Per unit",
  hourly: "Hourly",
  monthly: "Monthly",
  count: "Count",
};

const CATEGORY_LABELS = {
  assessment: "In-Depth Assessment",
  implementation: "Phase 1 Implementation",
  additional_workflow: "Additional Workflow",
  onsite_rate: "Onsite Hourly Rate",
  included_workflows: "Included Workflows",
};

export default function PricingConfig({ session }) {
  const [pricing, setPricing] = useState([]);
  const [retainers, setRetainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, rRes] = await Promise.all([
        fetch(`${API}/api/admin/pricing`),
        fetch(`${API}/api/admin/retainer-plans`),
      ]);
      setPricing(await pRes.json());
      setRetainers(await rRes.json());
    } catch {
      toast.error("Failed to load pricing data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updatePricing = (id, field, value) => {
    setPricing((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [field]: value } : p))
    );
  };

  const updateRetainer = (id, field, value) => {
    setRetainers((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    );
  };

  const savePricing = async (item) => {
    setSavingId(item.id);
    try {
      const res = await fetch(`${API}/api/admin/pricing/${item.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          label: item.label,
          category: item.category,
          amount: parseFloat(item.amount),
          billing_type: item.billing_type,
          description: item.description,
          is_active: item.is_active,
        }),
      });
      if (!res.ok) throw new Error();
      toast.success(`${item.label} saved`);
    } catch {
      toast.error("Failed to save");
    } finally {
      setSavingId(null);
    }
  };

  const saveRetainer = async (plan) => {
    setSavingId(plan.id);
    try {
      const res = await fetch(`${API}/api/admin/retainer-plans/${plan.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan_name: plan.plan_name,
          monthly_price: parseFloat(plan.monthly_price),
          short_label: plan.short_label,
          summary_description: plan.summary_description,
          included_services_html: plan.included_services_html,
          sort_order: plan.sort_order,
          is_active: plan.is_active,
          is_featured: plan.is_featured,
        }),
      });
      if (!res.ok) throw new Error();
      toast.success(`${plan.plan_name} plan saved`);
    } catch {
      toast.error("Failed to save retainer plan");
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="py-10" data-testid="pricing-config-page">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Settings2 className="h-6 w-6 text-[var(--brand)]" />
            <h1 className="text-2xl font-semibold text-foreground">
              Pricing Configuration
            </h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage all pricing centrally. Changes apply across the landing page,
            Scope of Services, and admin screens.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchData}
          className="gap-2"
          data-testid="refresh-pricing"
        >
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {/* Service Pricing */}
      <div className="mb-10">
        <h2 className="mb-4 text-lg font-semibold text-foreground">
          Service Pricing
        </h2>
        <div className="grid gap-4 lg:grid-cols-2">
          {pricing.map((item) => (
            <Card
              key={item.id}
              className="border-border bg-card p-5"
              data-testid={`pricing-card-${item.category}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-[var(--brand)]" />
                  <span className="text-sm font-semibold text-foreground">
                    {CATEGORY_LABELS[item.category] || item.category}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className="text-xs"
                  >
                    {BILLING_LABELS[item.billing_type] || item.billing_type}
                  </Badge>
                  <Switch
                    checked={item.is_active}
                    onCheckedChange={(v) => updatePricing(item.id, "is_active", v)}
                    data-testid={`toggle-${item.category}`}
                  />
                </div>
              </div>

              <Separator className="my-3 bg-border" />

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-xs text-muted-foreground">Label</Label>
                  <Input
                    value={item.label}
                    onChange={(e) => updatePricing(item.id, "label", e.target.value)}
                    className="mt-1 h-9 border-border bg-background text-foreground"
                    data-testid={`label-${item.category}`}
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    {item.billing_type === "count" ? "Count" : "Amount ($)"}
                  </Label>
                  <Input
                    type="number"
                    value={item.amount}
                    onChange={(e) => updatePricing(item.id, "amount", e.target.value)}
                    className="mt-1 h-9 border-border bg-background text-foreground"
                    data-testid={`amount-${item.category}`}
                  />
                </div>
              </div>

              <div className="mt-3">
                <Label className="text-xs text-muted-foreground">Description</Label>
                <Textarea
                  value={item.description}
                  onChange={(e) => updatePricing(item.id, "description", e.target.value)}
                  rows={2}
                  className="mt-1 border-border bg-background text-foreground text-sm"
                />
              </div>

              <div className="mt-3 flex justify-end">
                <Button
                  size="sm"
                  onClick={() => savePricing(item)}
                  disabled={savingId === item.id}
                  className="gap-2 bg-[var(--brand)] text-white hover:bg-[var(--brand)]/90"
                  data-testid={`save-${item.category}`}
                >
                  {savingId === item.id ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Save className="h-3 w-3" />
                  )}
                  Save
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Retainer Plans */}
      <div>
        <h2 className="mb-4 text-lg font-semibold text-foreground">
          Retainer Plans
        </h2>
        <p className="mb-4 text-sm text-muted-foreground">
          These plans are offered to customers after implementation is complete.
          The lowest active plan price is shown on the landing page as "Starting
          at".
        </p>
        <div className="grid gap-5 lg:grid-cols-3">
          {retainers.map((plan) => (
            <Card
              key={plan.id}
              className={`border-border bg-card p-5 ${
                plan.is_featured
                  ? "ring-1 ring-[var(--brand)]/40"
                  : ""
              }`}
              data-testid={`retainer-card-${plan.plan_code}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-base font-semibold text-foreground">
                      {plan.plan_name}
                    </span>
                    {plan.is_featured && (
                      <Star className="h-4 w-4 fill-[var(--brand)] text-[var(--brand)]" />
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {plan.short_label}
                  </p>
                </div>
                <Switch
                  checked={plan.is_active}
                  onCheckedChange={(v) => updateRetainer(plan.id, "is_active", v)}
                  data-testid={`toggle-retainer-${plan.plan_code}`}
                />
              </div>

              <Separator className="my-3 bg-border" />

              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Plan Name</Label>
                  <Input
                    value={plan.plan_name}
                    onChange={(e) => updateRetainer(plan.id, "plan_name", e.target.value)}
                    className="mt-1 h-9 border-border bg-background text-foreground"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Monthly Price ($)
                  </Label>
                  <Input
                    type="number"
                    value={plan.monthly_price}
                    onChange={(e) =>
                      updateRetainer(plan.id, "monthly_price", e.target.value)
                    }
                    className="mt-1 h-9 border-border bg-background text-foreground"
                    data-testid={`price-retainer-${plan.plan_code}`}
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Summary Description
                  </Label>
                  <Textarea
                    value={plan.summary_description}
                    onChange={(e) =>
                      updateRetainer(plan.id, "summary_description", e.target.value)
                    }
                    rows={2}
                    className="mt-1 border-border bg-background text-foreground text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Included Services (HTML)
                  </Label>
                  <Textarea
                    value={plan.included_services_html}
                    onChange={(e) =>
                      updateRetainer(plan.id, "included_services_html", e.target.value)
                    }
                    rows={4}
                    className="mt-1 border-border bg-background text-foreground font-mono text-xs"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={plan.is_featured}
                      onCheckedChange={(v) =>
                        updateRetainer(plan.id, "is_featured", v)
                      }
                    />
                    <Label className="text-xs text-muted-foreground">
                      Featured
                    </Label>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      Order
                    </Label>
                    <Input
                      type="number"
                      value={plan.sort_order}
                      onChange={(e) =>
                        updateRetainer(plan.id, "sort_order", parseInt(e.target.value) || 0)
                      }
                      className="ml-1 inline h-8 w-16 border-border bg-background text-foreground text-xs"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <Button
                  size="sm"
                  onClick={() => saveRetainer(plan)}
                  disabled={savingId === plan.id}
                  className="gap-2 bg-[var(--brand)] text-white hover:bg-[var(--brand)]/90"
                  data-testid={`save-retainer-${plan.plan_code}`}
                >
                  {savingId === plan.id ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Save className="h-3 w-3" />
                  )}
                  Save
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
